/**
 * CareLogix™ ORACLE — AdminOne API Client Guard (additive).
 *
 * Installs a global fetch interceptor that blocks any call to AdminOne /
 * admin / governance / internal-audit endpoints when the current page is
 * NOT under /adminone or /admin. This is a UX/devtools-hygiene layer; the
 * primary security boundary is the backend lockdown middleware.
 *
 * Blocked calls return a synthetic 451 ("Unavailable For Legal Reasons")
 * Response so component code that ignores the rejection doesn't crash.
 *
 * Triggered once on app boot via App.js.
 */

const LOCKED_PREFIXES = [
  "/api/adminone/",
  "/api/admin/",
  "/api/governance/",
  "/api/oracle/admin/",
  "/api/membership/admin/",
  "/api/telemetry/admin/",
  "/api/audit/",
  "/api/provider/internal/",
];

const ANON_ALLOW = [
  "/api/adminone/auth/login",
  "/api/adminone/auth/refresh",
  "/api/adminone/auth/2fa/setup",
  "/api/adminone/auth/2fa/verify",
];

function _isLockedPath(path) {
  if (!path) return false;
  for (const allow of ANON_ALLOW) {
    if (path.startsWith(allow)) return false;
  }
  return LOCKED_PREFIXES.some((p) => path.includes(p));
}

function _isAdminPage() {
  try {
    const p = window.location.pathname || "";
    return p.startsWith("/adminone") || p.startsWith("/admin");
  } catch { return false; }
}

let _installed = false;

export function installAdminOneApiGuard() {
  if (_installed) return;
  if (typeof window === "undefined" || !window.fetch) return;
  _installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function guardedFetch(input, init) {
    try {
      let url = typeof input === "string" ? input : (input && input.url) || "";
      if (_isLockedPath(url) && !_isAdminPage()) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error(`[AdminOneApiGuard] Blocked AdminOne API call from user route: ${url}`);
        }
        return new Response(
          JSON.stringify({
            detail: "AdminOne API blocked from non-admin route.",
            error:  "ADMINONE_API_BLOCKED_CLIENT_SIDE",
            code:   451,
          }),
          { status: 451, headers: { "Content-Type": "application/json" } },
        );
      }
    } catch { /* never break fetch */ }
    return originalFetch(input, init);
  };
}
